/**
 * Display formatting shared by desktop and mobile (SPEC §17.4 "Numbers formatting", Tradesea chip
 * conventions from docs/DESIGN-REFERENCE.md). Pure functions: no DOM, unit-tested.
 */

/** Shown when a value is not known yet (no account / no feed): Tradesea's `$ --`. */
export const MONEY_PLACEHOLDER = "$ --";
/** Privacy mask for balances and P&L (Tradesea `***`). */
export const PRIVACY_MASK = "***";
/** Real minus sign (U+2212), SPEC §17.4. */
export const MINUS = "−";

export type MoneyOptions = {
  /** Mask the value (privacy mode). A placeholder stays a placeholder. */
  privacy?: boolean;
  /** Prefix positive values with "+" (P&L). Balances are unsigned. */
  signed?: boolean;
  /** Fraction digits; default 2. Chips use 0 when the value is large. */
  decimals?: number;
};

/** `+$1,240.00` / `−$35.00` / `$51,240.00` / `$ --` / `***`. */
export function formatMoney(value: number | null | undefined, opts: MoneyOptions = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return MONEY_PLACEHOLDER;
  if (opts.privacy === true) return PRIVACY_MASK;
  const decimals = opts.decimals ?? 2;
  const abs = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  // A value that rounds to zero shows no sign.
  const isZero = Number(Math.abs(value).toFixed(decimals)) === 0;
  if (value < 0 && !isZero) return `${MINUS}$${abs}`;
  if (opts.signed === true && value > 0 && !isZero) return `+$${abs}`;
  return `$${abs}`;
}

export type CentsOptions = {
  privacy?: boolean;
  /** Prefix positive values with "+" (P&L). */
  signed?: boolean;
  /** Drop ".00" on whole dollars (narrow columns): `+$250` but `+$1.25`. */
  trimZeroCents?: boolean;
};

/**
 * Integer cents → `+$1,240.50` / `−$35.00` / `$ --`. Exact: string math on the integer, never a
 * float division, so any cents value (bigint or safe-integer number) prints exactly.
 */
export function formatCents(cents: bigint | number | null | undefined, opts: CentsOptions = {}): string {
  if (cents === null || cents === undefined) return MONEY_PLACEHOLDER;
  if (typeof cents === "number" && !Number.isSafeInteger(cents)) return MONEY_PLACEHOLDER;
  if (opts.privacy === true) return PRIVACY_MASK;
  const c = BigInt(cents);
  const abs = c < 0n ? -c : c;
  const dollars = (abs / 100n).toLocaleString("en-US");
  const rem = abs % 100n;
  const body =
    opts.trimZeroCents === true && rem === 0n ? dollars : `${dollars}.${rem.toString().padStart(2, "0")}`;
  if (c < 0n) return `${MINUS}$${body}`;
  if (opts.signed === true && c > 0n) return `+$${body}`;
  return `$${body}`;
}

export type Tone = "pos" | "neg" | "zero" | "none";

/** P&L colour rule (Tradesea): green > 0, red < 0, grey = 0, none when unknown. */
export function pnlTone(value: number | null | undefined): Tone {
  if (value === null || value === undefined || !Number.isFinite(value)) return "none";
  if (value > 0) return "pos";
  if (value < 0) return "neg";
  return "zero";
}

/** Instrument price-format facts (from `InstrumentInfo`). */
export type PriceFormat = {
  tickSizeNum: number;
  tickSizeDen: number;
  displayDecimals: number;
};

/** Tick size as a decimal (e.g. 1/4 → 0.25). */
export function tickSize(f: PriceFormat): number {
  return f.tickSizeDen === 0 ? 0 : f.tickSizeNum / f.tickSizeDen;
}

/** Integer ticks → price number. */
export function ticksToPrice(ticks: number, f: PriceFormat): number {
  return (ticks * f.tickSizeNum) / (f.tickSizeDen === 0 ? 1 : f.tickSizeDen);
}

/** Integer ticks → display string with the instrument's decimals and thousands separators. */
export function formatTicks(ticks: number | null | undefined, f: PriceFormat | null): string {
  if (ticks === null || ticks === undefined || f === null) return "—";
  return ticksToPrice(ticks, f).toLocaleString("en-US", {
    minimumFractionDigits: f.displayDecimals,
    maximumFractionDigits: f.displayDecimals,
  });
}

/** Compact volume: 950, 12.4K, 3.1M. */
export function formatVolume(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (v < 1000) return String(Math.round(v));
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}K`;
  return `${(v / 1_000_000).toFixed(1)}M`;
}

/** Signed percentage with a real minus: `+0.53%`, `−0.86%`. */
export function formatPercent(v: number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const s = Math.abs(v).toFixed(decimals);
  if (Number(s) === 0) return `${s}%`;
  return `${v < 0 ? MINUS : "+"}${s}%`;
}
